import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ScheduleBookingForm from "../components/landing/ScheduleBookingForm";
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import usePageSeo from "../hooks/usePageSeo";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import { WORKFLOW_BUILDER_FAQS } from "../data/seo/faqs";
import { buildServiceSchema } from "../data/seo/structuredData";

const keyFeatures = [
    {
        title: "Drag-to-Connect Canvas",
        description: "Visual graph editor — drag nodes, connect edges, fan out, merge back. The same canvas powers chat and voice flows.",
    },
    {
        title: "Branching & Guards",
        description: "Conditional edges with guards (intent, score, language, channel, custom variables) keep complex flows readable.",
    },
    {
        title: "Tool Calls Built-In",
        description: "First-class nodes for Knowledge Base lookup, calendars, Shopify, SMS/email, webhooks, and your own custom HTTP tools.",
    },
    {
        title: "Realtime Preview",
        description: "Test execution paths inside the builder — type a sample input and watch every node fire, with timings and outputs inline.",
    },
    {
        title: "Versioning & Rollback",
        description: "Every publish creates a version. Compare diffs, roll back instantly, or A/B route traffic between two versions.",
    },
    {
        title: "Reusable Sub-Flows",
        description: "Encapsulate logic — escalation, identity verification, payment capture — into sub-flows you can drop into any workflow.",
    },
    {
        title: "Type-Safe Node Configs",
        description: "Dynamic node config UIs are generated from backend schemas, so a typo never reaches production.",
    },
    {
        title: "Multi-Agent Orchestration",
        description: "Hand off between specialized agents — sales, support, scheduler — each with its own knowledge and tools.",
    },
    {
        title: "Templates & Vertical Packs",
        description: "Start from Dental, Real Estate, Retail, or Support packs — or import a JSON workflow from another team.",
    },
];

const nodeTypes = [
    { name: "Start", color: "from-emerald-500 to-teal-500" },
    { name: "LLM", color: "from-indigo-500 to-purple-500" },
    { name: "KB Search", color: "from-orange-500 to-pink-500" },
    { name: "Tool Call", color: "from-blue-500 to-cyan-500" },
    { name: "Condition", color: "from-yellow-500 to-orange-500" },
    { name: "End", color: "from-slate-500 to-slate-700" },
];

const buildSteps = [
    {
        step: "1",
        title: "Pick a template",
        description: "Start from a vertical pack — or a blank canvas. Templates ship with sensible defaults and example flows.",
    },
    {
        step: "2",
        title: "Drag nodes & connect",
        description: "Pull nodes from the palette, wire them together, configure each one with a side-panel form.",
    },
    {
        step: "3",
        title: "Test in the builder",
        description: "Run sample inputs and step through execution live. Inspect every variable as it flows through the graph.",
    },
    {
        step: "4",
        title: "Publish a version",
        description: "Ship to production with one click. Roll back any time. Diff versions to see what changed.",
    },
];

const primaryKeywords = [
    "AI workflow builder",
    "Conversation flow designer",
    "Visual chatbot builder",
    "AI agent orchestration",
    "No-code AI workflow",
    "LangChain alternative",
    "Agent flow editor",
    "Drag-and-drop AI builder",
];

export default function WorkflowBuilderMarketingPage() {
    usePageSeo({
        title: "AI Workflow Builder — Design Conversations Visually | FloGPT",
        description:
            "Drag-to-connect AI workflow builder for chat and voice agents. Branching, tool calls, KB lookups, and versioning. Design conversations like a flowchart and publish with one click.",
        keywords: primaryKeywords.join(", "),
        canonical: "/ai-workflow-builder",
    });

    const serviceSchema = useMemo(
        () =>
            buildServiceSchema({
                name: "FloGPT AI Workflow Builder",
                description:
                    "Visual drag-to-connect editor for designing chat and voice agent workflows. Conditional branching, tool calls, KB lookups, multi-agent orchestration, and one-click versioned publish.",
                path: "/ai-workflow-builder",
                serviceType: "AI Workflow / Agent Orchestration",
                features: [
                    "Drag-to-connect visual canvas",
                    "Branching with intent + variable guards",
                    "Built-in nodes: LLM, KB Search, Tool Call, HTTP, Condition",
                    "Realtime in-builder execution preview",
                    "Versioning, diffs, and rollback",
                    "Reusable sub-flows",
                    "Multi-agent orchestration",
                    "Vertical pack templates",
                ],
            }),
        [],
    );

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            <main>
                {/* HERO */}
                <section className="relative overflow-hidden border-b border-slate-900 pt-24 sm:pt-28" id="overview">
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-indigo-500/20 blur-3xl" />
                        <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-purple-500/10 blur-3xl" />
                        <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 lg:py-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                            <motion.div
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300 mb-6">
                                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" />
                                    Workflow Builder · Visual · Versioned
                                </div>
                                <h1 className="text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-[3.35rem] lg:leading-[1.05]">
                                    Design Conversations Like a{" "}
                                    <motion.span
                                        className="inline-block"
                                        animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                        style={{
                                            background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899, #6366f1)",
                                            backgroundSize: "200% 100%",
                                            WebkitBackgroundClip: "text",
                                            WebkitTextFillColor: "transparent",
                                            backgroundClip: "text",
                                        }}
                                    >
                                        Flowchart
                                    </motion.span>
                                </h1>
                                <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
                                    Drag to connect nodes, branch on conditions, call your own tools, and publish a new version in one click. The same builder powers your chat and voice agents.
                                </p>
                                <div className="mt-8 flex flex-wrap items-center gap-4">
                                    <Link
                                        to="/signup"
                                        className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-7 py-3 text-sm font-semibold text-white shadow-lg shadow-orange-500/20 transition-transform hover:-translate-y-0.5"
                                    >
                                        Start Building Free
                                    </Link>
                                    <a
                                        href="#book-free-demo"
                                        className="inline-flex items-center justify-center rounded-xl border border-white/15 bg-white/5 px-7 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                                    >
                                        Book a Demo
                                    </a>
                                </div>
                                <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-400">
                                    <span>· Drag-to-connect</span>
                                    <span>· Versioning + rollback</span>
                                    <span>· Tool calls built-in</span>
                                </div>
                            </motion.div>

                            {/* Animated workflow preview */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.97 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                className="relative"
                            >
                                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-r from-indigo-500/25 to-purple-500/25 blur-2xl" />
                                <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl p-6 sm:p-8">
                                    <div className="grid grid-cols-3 gap-4">
                                        {nodeTypes.map((n, idx) => (
                                            <motion.div
                                                key={n.name}
                                                initial={{ opacity: 0, y: 10 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.05 * idx }}
                                                viewport={{ once: true }}
                                                className={`rounded-xl border border-slate-800 bg-gradient-to-r ${n.color} p-4 text-center text-sm font-semibold text-white shadow-lg`}
                                            >
                                                {n.name}
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300 font-mono">
                                        {[
                                            "if (intent == 'pricing') -> node:SendPricing",
                                            "elif (score > 0.7) -> node:AnswerFromKB",
                                            "elif (channel == 'voice') -> node:TransferToHuman",
                                            "else -> node:Clarify",
                                        ].map((line, i) => (
                                            <div key={i} className="flex items-start gap-3">
                                                <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                                                <span className="whitespace-pre-wrap">{line}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4 text-xs text-slate-400">
                                        <span>Workflow: Lead Qualifier v7</span>
                                        <span>Published 2 min ago</span>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* WHY DIFFERENT */}
                <section className="mx-auto max-w-4xl px-6 lg:px-8 py-14 lg:py-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="space-y-6"
                    >
                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-indigo-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                                    Stop hand-rolling agent code.
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Hard-coded agent flows are fast to write — and a nightmare to evolve. Every product ask becomes an engineering ticket.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">
                                FloGPT's workflow builder gives PMs and ops teams a canvas to design, test, and ship flows safely — while engineering keeps full control of tool nodes and guards.
                            </p>
                        </article>

                        <article className="rounded-2xl border border-slate-800 bg-slate-900/70 p-7 shadow-lg shadow-black/20">
                            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                                <span className="bg-gradient-to-r from-indigo-300 via-pink-300 to-orange-300 bg-clip-text text-transparent">
                                    Why this isn't a "drag-and-drop bot builder"
                                </span>
                            </h2>
                            <p className="mt-4 text-slate-300 leading-relaxed text-lg">
                                Most "no-code" bot builders are decision trees with extra steps.
                            </p>
                            <p className="mt-4 text-slate-200 leading-relaxed">FloGPT's workflow builder:</p>
                            <ul className="mt-4 space-y-3 text-slate-200">
                                {[
                                    "Treats LLMs, KB lookups, and tools as first-class nodes",
                                    "Has versioning, rollback, and traffic-split A/B built in",
                                    "Type-checks every node config against the live backend schema",
                                    "Re-uses the same flow across chat and voice channels",
                                ].map((line) => (
                                    <li key={line} className="flex items-start gap-3">
                                        <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rotate-45 rounded-[2px] bg-gradient-to-br from-indigo-400 via-pink-400 to-orange-400 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-slate-100 font-medium">It's the agent runtime your engineering team would have built — without the timeline.</p>
                        </article>
                    </motion.div>
                </section>

                {/* BUILD STEPS */}
                <section className="relative overflow-hidden">
                    <div className="absolute inset-0 -z-10 opacity-30">
                        <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
                        <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
                    </div>
                    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
                        <div className="text-center max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold text-white">From idea to live agent in four steps</h2>
                            <p className="mt-4 text-slate-300">
                                Your first workflow is a coffee break, not a sprint.
                            </p>
                        </div>

                        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                            {buildSteps.map((s) => (
                                <motion.article
                                    key={s.step}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:border-indigo-400/40 hover:bg-white/10 transition-all"
                                >
                                    <div className="text-sm font-semibold text-indigo-300">Step {s.step}</div>
                                    <h3 className="mt-2 text-lg font-semibold text-white">{s.title}</h3>
                                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.description}</p>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* KEY FEATURES */}
                <section className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl font-bold text-white">Everything the workflow builder does</h2>
                        <p className="mt-4 text-slate-300">
                            One canvas. Every channel. Production-grade from day one.
                        </p>
                    </div>

                    <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {keyFeatures.map((feature, index) => (
                            <article
                                key={feature.title}
                                className="group relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 p-6 shadow-lg shadow-black/20 transition-all duration-300 hover:-translate-y-1 hover:border-indigo-400/40"
                            >
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-500" />
                                <div className="flex items-center justify-between">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-500/15 border border-indigo-400/30">
                                        <svg className="h-5 w-5 text-indigo-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-slate-300">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                </div>

                                <h3 className="mt-5 text-lg font-semibold text-white">{feature.title}</h3>
                                <p className="mt-3 text-slate-300 leading-relaxed">{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </section>

                {/* RELATED LINKS */}
                <section className="mx-auto max-w-5xl px-6 lg:px-8 py-12">
                    <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7">
                        <h3 className="text-lg font-semibold text-white">Workflow Builder powers</h3>
                        <p className="mt-2 text-slate-400 text-sm">The same flows ship to chat and to voice — design once, run anywhere.</p>
                        <div className="mt-5 grid gap-4 md:grid-cols-3">
                            <Link to="/ai-chat-widget" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Chat Widget →</div>
                                <p className="mt-1 text-sm text-slate-400">Embed a chat agent driven by your workflow on any site.</p>
                            </Link>
                            <Link to="/ai-call-pipeline" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Call Pipeline →</div>
                                <p className="mt-1 text-sm text-slate-400">Run the same flow as a voice agent on a phone number.</p>
                            </Link>
                            <Link to="/ai-knowledge-base" className="rounded-xl border border-slate-800 bg-slate-900/70 p-5 hover:border-indigo-400/40 transition-all group">
                                <div className="text-sm font-semibold text-white">Knowledge Base →</div>
                                <p className="mt-1 text-sm text-slate-400">Ground every node's answers in your real docs and FAQs.</p>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* CTA + BOOKING */}
                <section id="book-free-demo" className="mx-auto max-w-6xl px-6 lg:px-8 pb-20">
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white">See your workflow come to life</h2>
                        <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                            Book a free 20-minute consultation. We'll build a working flow from your existing process — live, on the call.
                        </p>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, amount: 0.2 }}
                        className="mt-12 max-w-4xl mx-auto"
                    >
                        <ScheduleBookingForm
                            title="Book a Free Workflow Builder Demo"
                            subtitle="Bring your support, sales, or booking process — we'll model it live"
                            submitLabel="Book Free Demo"
                        />
                    </motion.div>
                </section>

                <FAQSection
                    id="workflow-builder"
                    eyebrow="Workflow Builder · FAQ"
                    heading="Common questions about the workflow builder"
                    items={WORKFLOW_BUILDER_FAQS}
                />
            </main>
            <Footer />

            <JsonLd id="service-workflow-builder" data={serviceSchema} />
        </div>
    );
}
